import { useContext } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { BrokerPortfolioTypesEnum, DashFeaturesEnum } from "dash-common";
import {
  DropDown,
  Flex,
  IconKeys,
  ModalContext,
  ViewerContext,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";

import { PortfolioListItemFragment } from "gql/generated.graphql";
import {
  getPreviewPortfolioPropertyLink,
  getPreviewPortfolioLink,
} from "common/helpers/portfolio";

export const FixedContainer = styled(Flex)`
  position: absolute;
  right: ${({ theme }) => theme.space * 3}px;
  top: ${({ theme }) => theme.space * 3}px;
  z-index: 2;
`;

const PortfolioActionsDropDown = ({
  portfolio,
  Anchor,
  isFixed = true,
}: {
  portfolio: PortfolioListItemFragment;
  Anchor: () => JSX.Element;
  isFixed?: boolean;
}) => {
  const { t } = useTranslation("pages");
  const { toggleModal, setPayload } = useContext(ModalContext);
  const isFolder = portfolio.type === BrokerPortfolioTypesEnum.FOLDER;
  const { broker, profile } = useContext(ViewerContext);

  const DropdownContainer = isFixed ? FixedContainer : Flex;

  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
  ]);

  return (
    <DropdownContainer>
      <DropDown
        Anchor={Anchor}
        variant="dark"
        placementHorizontal="left"
        items={[
          ...(portfolio?.isAddedMediaKit
            ? [
                {
                  icon: "backupTable" as IconKeys,
                  key: "backupTable",
                  children: t("pages:mediaKit.manageMediaKit"),
                  onClick: () => {
                    if (checkFeatureAccess()) {
                      setPayload({ portfolioId: portfolio._id });
                      toggleModal(true, "ManageMediaKitModal");
                    }
                  },
                },
              ]
            : []),
          {
            icon: "visibility",
            key: "visibility",
            children: t("pages:portfoliosPage.viewSharedPortfolio"),
            onClick: () => {
              if (isFolder) {
                return window?.open(
                  getPreviewPortfolioLink(portfolio, broker, profile)
                );
              }
              window?.open(
                getPreviewPortfolioPropertyLink(portfolio, broker, profile),
                "_blank"
              );
            },
          },
          {
            icon: "share",
            children: t("common:labels.share"),
            key: "share",
            onClick: () => {
              if (checkFeatureAccess()) {
                toggleModal(true, "SharePortfolioModal");
                setPayload({ portfolio });
              }
            },
          },
          ...(isFolder
            ? []
            : [
                {
                  icon: "driveFileMove" as IconKeys,
                  key: "driveFileMove",
                  children: t("pages:portfolioDetailsPage:labels:moveProperty"),
                  onClick: () => {
                    if (checkFeatureAccess()) {
                      toggleModal(true, "MovePropertyToFolderModal");
                      setPayload({ portfolioIds: [portfolio?._id] });
                    }
                  },
                },
              ]),
          {
            icon: "trash",
            key: "trash",
            children: t("common:labels.delete"),
            onClick: () => {
              if (!isFolder) {
                if (checkFeatureAccess()) {
                  toggleModal(true, "RemovePropertyFromPortfolio");
                  setPayload({
                    portfolioId: portfolio._id,
                  });
                }
              } else {
                if (checkFeatureAccess()) {
                  toggleModal(true, "RemoveFolderFromPortfolio");
                  setPayload({
                    folderId: portfolio._id,
                  });
                }
              }
            },
          },
        ]}
      />
    </DropdownContainer>
  );
};

export default PortfolioActionsDropDown;
